import { useDragDropMonitor } from '@dnd-kit/react';
import { FC, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { computeDropBand, type DropBand } from './treeUtils';

/** Геометрия индикатора места вставки в координатах контейнера дерева блоков (px). */
type IndicatorGeometry = {
  /** Смещение верхней границы строки-цели относительно контейнера */
  top: number;
  /** Смещение левой границы строки-цели относительно контейнера */
  left: number;
  /** Ширина строки-цели */
  width: number;
  /** Высота строки-цели */
  height: number;
  /** Зона вставки: до/после строки или вложение внутрь листа */
  band: DropBand;
};

type Props = {
  /** Контейнер дерева блоков — относительно него абсолютно позиционируется индикатор */
  containerRef: RefObject<HTMLDivElement | null>;
  /** nodeId листовых блоков — только они допускают вложение внутрь по середине строки */
  leafNodeIds: Set<string>;
  /** nodeId свёрнутых блоков с детьми — цели авто-разворачивания при задержке наведения */
  collapsedGroupIds: Set<string>;
  /** nodeId → множество его потомков — чтобы не авто-разворачивать собственное поддерево источника */
  descendantsByNodeId: Map<string, Set<string>>;
  /** Разворачивает свёрнутый блок-цель по задержке наведения */
  onAutoExpand: (nodeId: string) => void;
};

/** Горизонтальный отступ линии от краёв строки (согласован с внутренними отступами строк дерева). */
const LINE_INSET = 8;

/** Задержка наведения (мс), после которой свёрнутый блок-цель авто-разворачивается под указателем. */
const AUTO_EXPAND_DWELL_MS = 500;

const areGeometriesEqual = (a: IndicatorGeometry | null, b: IndicatorGeometry | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height && a.band === b.band;
};

/**
 * Единый индикатор места вставки для всего дерева блоков отчёта.
 *
 * Ведётся из одной подписки на перемещение указателя (вместо подписки в каждом узле), поэтому
 * перемещение курсора не прогоняет обработчик по всем смонтированным блокам — на большом заключении
 * и слабом CPU перетаскивание остаётся отзывчивым. Рисуется отдельным слоем над деревом: контур
 * вложения вписан внутрь границ строки-цели (не обрезается контейнером на глубоких уровнях), а линия
 * вставки — заметной толщины и читается на узких вложенных строках. Плавающая копия перетаскиваемого
 * блока полупрозрачна, поэтому индикатор под ней остаётся виден.
 */
export const DropIndicator: FC<Props> = ({
  containerRef,
  leafNodeIds,
  collapsedGroupIds,
  descendantsByNodeId,
  onAutoExpand,
}) => {
  const [geometry, setGeometry] = useState<IndicatorGeometry | null>(null);

  // Таймер задержки наведения для авто-разворачивания: цель и её отложенный запуск.
  const dwellRef = useRef<{ targetId: string; timeoutId: ReturnType<typeof setTimeout> } | null>(null);
  const clearDwell = useCallback(() => {
    if (dwellRef.current) {
      clearTimeout(dwellRef.current.timeoutId);
      dwellRef.current = null;
    }
  }, []);

  const handlers = useMemo<Parameters<typeof useDragDropMonitor>[0]>(
    () => ({
      onDragMove: (_event, manager) => {
        const operation = manager.dragOperation;
        const { source, target } = operation;
        const container = containerRef.current;
        const rect = target?.shape?.boundingRectangle;
        const pointerY = operation.position.current?.y;
        // Нет цели/геометрии либо указатель над самим перетаскиваемым блоком — индикатор скрыт
        if (
          !source ||
          !target ||
          !container ||
          !rect ||
          pointerY == null ||
          String(target.id) === String(source.id)
        ) {
          clearDwell();
          setGeometry((prev) => (prev === null ? prev : null));
          return;
        }
        const sourceId = String(source.id);
        const targetId = String(target.id);

        // Авто-разворачивание: если цель — свёрнутый блок с детьми (и не собственное поддерево
        // источника), запускаем таймер задержки; при смене цели таймер сбрасывается, поэтому блоки,
        // над которыми курсор лишь проходит, не раскрываются, а раскрытый блок остаётся раскрытым.
        const canAutoExpand =
          collapsedGroupIds.has(targetId) && !descendantsByNodeId.get(sourceId)?.has(targetId);
        if (canAutoExpand) {
          if (dwellRef.current?.targetId !== targetId) {
            clearDwell();
            dwellRef.current = {
              targetId,
              timeoutId: setTimeout(() => {
                dwellRef.current = null;
                onAutoExpand(targetId);
              }, AUTO_EXPAND_DWELL_MS),
            };
          }
        } else {
          clearDwell();
        }

        const allowNest = leafNodeIds.has(targetId);
        const band = computeDropBand(pointerY, rect.top, rect.height, allowNest);
        const containerRect = container.getBoundingClientRect();
        const next: IndicatorGeometry = {
          top: rect.top - containerRect.top,
          left: rect.left - containerRect.left,
          width: rect.width,
          height: rect.height,
          band,
        };
        setGeometry((prev) => (areGeometriesEqual(prev, next) ? prev : next));
      },
      onDragEnd: () => {
        clearDwell();
        setGeometry(null);
      },
    }),
    [containerRef, leafNodeIds, collapsedGroupIds, descendantsByNodeId, onAutoExpand, clearDwell],
  );
  useDragDropMonitor(handlers);

  if (!geometry) return null;

  // Вложение внутрь листа: контур по границам строки-цели, вписанный внутрь рамки (не обрезается)
  if (geometry.band === 'nest') {
    return (
      <div
        aria-hidden
        className="pointer-events-none absolute z-30 rounded-md border-2 border-dashed border-primary bg-primary/10"
        style={{
          top: geometry.top + 1,
          left: geometry.left + 1,
          width: Math.max(0, geometry.width - 2),
          height: Math.max(0, geometry.height - 2),
        }}
      />
    );
  }

  // Вставка до/после строки-цели (в т.ч. «в конец уровня») — заметная линия у нужной границы
  const lineTop = geometry.band === 'after' ? geometry.top + geometry.height : geometry.top;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute z-30 h-1 -translate-y-1/2 rounded-full bg-primary"
      style={{
        top: lineTop,
        left: geometry.left + LINE_INSET,
        width: Math.max(0, geometry.width - LINE_INSET * 2),
      }}
    />
  );
};
